import { z } from "zod";
import {
    EntityIndustry,
    EntityProfileIndustry,
    EntityProfileRevenueBracket,
    EntityProfileType,
    FirmSize,
    ProfessionalType,
} from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import { normalizeTaxLocation } from "@common/constants";
import { createAccountingMigrationBodySchema } from "./accounting-integration.schema";
import { coaSetupBodySchema } from "./coa-setup.schema";
import { usStateCodeSchema } from "./us-state.schema";

export const entitySchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    address: z
        .object({
            street: z.string().nullable(),
            // street2: z.string().nullable(),
            city: z.string().nullable(),
            state: z.string().nullable(),
            postalCode: z.string().nullable(),
            country: z.string().nullable(),
        })
        .nullable(),
    bookkeepingYears: z.array(z.number()),
    bookkeepingStart: z.string(),
    taxLocations: z.array(z.unknown()),
    onboardingComplete: z.boolean(),
    industry: z.nativeEnum(EntityIndustry),
    iconColor: z.string(),
    logo: z.string().nullable(),
    isBusiness: z.boolean(),
    isPersonal: z.boolean(),
    userId: z.number().nullable(),
    legalType: z.nativeEnum(EntityProfileType).nullable(),
    isMockEntity: z.boolean(),
    workspaceId: z.string(),
    openingBalanceSubmitted: z.boolean(),
    reportsReady: z.boolean(),
    federalTaxRate: z.number(),
    allowAITrainingOnTheirData: z.boolean(),
    hasCustomChartOfAccounts: z.boolean(),
});

export type EntitySchema = z.infer<typeof entitySchema>;

// Contract request/response schemas for entity API
export const entityProfileBodySchema = z
    .object({
        industries: z.array(z.nativeEnum(EntityProfileIndustry)).optional(),
        industryOther: z.string().optional(),
        firmSize: z.nativeEnum(FirmSize).optional(),
        professionalType: z.nativeEnum(ProfessionalType).optional(),
        isPartnership: z.boolean().optional(),
        ignoreWaitlist: z.boolean().optional(),
        partnershipPercentage: z.number().int().optional(),
        description: z.string().optional(),
        website: z.string().optional(),
        annualExpenses: z.number().optional(),
        annualRevenue: z.nativeEnum(EntityProfileRevenueBracket).optional(),
        waitlistComment: z.string().optional(),
        howDidYouLearn: z.string().optional(),
        payrollProviders: z.array(z.string()).optional(),
        paymentProcessorRequest: z.string().optional(),
        country: z.string().optional(),
        source: z.string().optional(),
    })
    .strict();

export const updateEntityBodySchema = z.object({
    name: z
        .string()
        .regex(/\S+/)
        .describe("Updated entity display name.")
        .optional(),
    legalType: z
        .nativeEnum(EntityProfileType)
        .describe("Updated legal entity type.")
        .optional(),
    profile: entityProfileBodySchema
        .describe("Updated entity profile details.")
        .optional(),
    allowAITrainingOnTheirData: z
        .boolean()
        .describe("Whether this entity permits Kick AI training on its data.")
        .optional(),
});

export const updateEntityBookkeepingStartDateBodySchema = z.object({
    bookkeepingStartDate: z
        .string()
        .date()
        .describe("First day Kick keeps books for the entity, as YYYY-MM-DD."),
});

const entitySearchWorkspaceIdsSchema = z.preprocess(
    (value) => (typeof value === "string" ? [value] : value),
    z.array(z.string().uuid()).min(1).max(50),
);

export const searchEntitiesQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    workspaceIds: entitySearchWorkspaceIdsSchema.optional(),
    organizationId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().max(500).optional(),
});

export const searchEntitiesRowSchema = z.object({
    entityId: z.number().int(),
    entityName: z.string(),
    workspaceId: z.string().uuid(),
    workspaceName: z.string(),
});

export const entityEmailAddressesSchema = z.object({
    slug: z.string(),
    receipts: z.string().email(),
    bills: z.string().email(),
    invoices: z.string().email(),
});

export const entityEmailAddressesResponseSchema = z.object({
    addresses: entityEmailAddressesSchema.nullable(),
});

export const createEntityBodySchema = z.object({
    name: z.string().min(1).describe("Entity display name."),
    workspaceId: z
        .string()
        .uuid()
        .describe("Kick workspace UUIDv7 where the entity will be created."),
    legalType: z
        .nativeEnum(EntityProfileType)
        .describe("Legal entity type.")
        .optional(),
    industry: z
        .nativeEnum(EntityIndustry)
        .describe("Entity industry.")
        .optional(),
    profile: entityProfileBodySchema
        .describe("Entity profile details.")
        .optional(),
    bookkeepingStartDate: z
        .string()
        .date()
        .describe("Bookkeeping start date in yyyy-MM-dd format.")
        .optional(),
    servicesOffered: z
        .array(z.nativeEnum(OrganizationServicesOffered))
        .describe("Accounting or tax services offered for this entity.")
        .optional(),
    migration: createAccountingMigrationBodySchema
        .describe("Optional accounting migration configuration.")
        .optional(),
    coaSetup: coaSetupBodySchema
        .describe(
            "Optional CoA seeding from a template or another entity. Applied before migration when both are provided.",
        )
        .optional(),
    isAccrualLedgerEnabled: z
        .boolean()
        .describe("Whether to enable accrual ledger setup for the entity.")
        .optional(),
});

export const searchEntitiesResponseSchema = z.object({
    rows: z.array(searchEntitiesRowSchema),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
});

export type SearchEntitiesQuery = z.infer<typeof searchEntitiesQuerySchema>;
export type SearchEntitiesResponse = z.infer<
    typeof searchEntitiesResponseSchema
>;

export const reorderAccountsBodySchema = z.object({
    entityId: z.coerce.number(),
    order: z.array(z.number().int()),
});

export const taxLocationSchema = z.object({
    location: z
        .string()
        .transform((value, ctx) => {
            const normalized = normalizeTaxLocation(value);
            if (!normalized) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        'location must be a US state (a 2-letter code like "CA" or a full name like "California") or "international" / "unknown".',
                });
                return z.NEVER;
            }
            return normalized;
        })
        .describe(
            'Tax location: a US state — either a 2-letter code ("CA") or a full name ("California"), which is normalized to the code — or "international" / "unknown".',
        ),
    start: z
        .string()
        .describe(
            "Optional start date in yyyy-MM-dd format; null or omit when open-ended.",
        )
        .nullish(),
    end: z
        .string()
        .describe(
            "Optional end date in yyyy-MM-dd format; null or omit when open-ended.",
        )
        .nullish(),
});

export const saveTaxLocationsBodySchema = z.object({
    entityId: z.coerce
        .number()
        .describe("Numeric Kick entity id from entities_query."),
    taxLocations: z
        .array(taxLocationSchema)
        .min(1)
        .describe("Complete tax location list to save for the entity."),
});

// Entity address body. Only US-based entities are supported for now, so
// `country` is not a client-settable field — it's always persisted as "US"
// and `state` is always required and normalized to a US state code.
export const entityAddressBodySchema = z
    .object({
        street: z
            .string()
            .min(1)
            .describe('Street address, e.g. "123 Market St".'),
        street2: z
            .string()
            .nullable()
            .optional()
            .describe('Optional second address line, e.g. "Suite 400".'),
        city: z.string().min(1).describe('City name, e.g. "San Francisco".'),
        postalCode: z.string().min(1).describe("Postal / ZIP code."),
        state: usStateCodeSchema,
    })
    .transform((value) => ({
        ...value,
        street2: value.street2 ?? null,
    }));

export const updateEntityAddressBodySchema = z.object({
    address: entityAddressBodySchema.describe("Complete replacement address."),
});

export const addressResponseSchema = z
    .object({
        id: z.number(),
        entityId: z.number(),
        street: z.string(),
        street2: z.string().nullable(),
        city: z.string(),
        postalCode: z.string(),
        state: z.string().nullable(),
        country: z.string(),
    })
    .nullable();

export const createLogoUploadUrlBodySchema = z.object({
    name: z.string(),
});

export const createLogoUploadUrlResponseSchema = z.object({
    url: z.string(),
    path: z.string(),
});

export const updateLogoBodySchema = z.object({
    logoPath: z.string().nullable(),
});

// Lightweight read used by the MCP/agent layer to resolve an entity's owning
// workspace and whether it operates in GL-first mode, so the agent
// classification guard can react without fetching the full entity payload.
export const entityMcpContextResponseSchema = z.object({
    entityId: z.number().int(),
    workspaceId: z.string().uuid(),
    glFirstEnabled: z.boolean(),
    legalType: z.nativeEnum(EntityProfileType).nullable(),
});
